/**
 * QuickScan — the destination of the PWA "Scan & log" shortcut. Opens the
 * camera scanner the instant the route mounts, then once the user decodes
 * (or types) an equipment barcode, presents a three-button quick-action
 * menu so they can decide in one tap what to do with that equipment:
 *
 *   • Log temperature   →  /temperature?equipment=<id>
 *   • Sign off cleaning →  /cleaning?equipment=<id>
 *   • View history       →  /temperature?equipment=<id>&filter=eq
 *
 * Designed to be used as a home-screen icon from the manifest's `shortcuts`
 * array, so kitchen staff go from "tap icon → scan sticker → log temp" in
 * three taps total during a busy service.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useSite } from "@/contexts/SiteContext";
import BarcodeScanner from "@/components/BarcodeScanner";
import { Button } from "@/components/ui/button";
import { Thermometer, Sparkles, History, ScanLine, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { looksLikeGs1, parseGs1 } from "@/lib/gs1";

export default function QuickScan() {
  const navigate = useNavigate();
  const { activeSite } = useSite();
  const [scannerOpen, setScannerOpen] = useState(true);
  const [equipment, setEquipment] = useState(null);

  // Reopen the scanner if the user dismisses the menu.
  useEffect(() => {
    if (!equipment && !scannerOpen) {
      const t = setTimeout(() => setScannerOpen(true), 150);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [equipment, scannerOpen]);

  const handleScan = async (code) => {
    setScannerOpen(false);
    if (!activeSite || !code) return;
    // 1. Verify-deep-link URL printed on our own labels (v2.5+). The QR
    //    encodes a public URL with `?id=<uuid>` plus GS1 query AIs —
    //    extract the label id and route straight to the Verify page.
    if (typeof code === "string" && /^https?:\/\//i.test(code) && /\/labels\/verify/i.test(code)) {
      try {
        const u = new URL(code);
        const id = u.searchParams.get("id");
        const b = u.searchParams.get("batch") || u.searchParams.get("10");
        if (id) {
          navigate(`/labels/verify?id=${encodeURIComponent(id)}`);
          return;
        }
        if (b) {
          navigate(`/labels/verify?batch=${encodeURIComponent(b)}`);
          return;
        }
      } catch { /* fall through to GS1 parsing below */ }
    }
    // 1b. Equipment deep-link QR printed on our own stickers (v2.24+).
    //     Encodes `/e/<id>` (or the long `/temperature?equipment=<id>`).
    //     Scanning it in-app should jump straight to the pre-filled temp
    //     form for that equipment.
    if (typeof code === "string" && /^https?:\/\//i.test(code)) {
      try {
        const u = new URL(code);
        const eqMatch = u.pathname.match(/\/e\/([^/?#]+)/i);
        const eqId = eqMatch ? decodeURIComponent(eqMatch[1]) : u.searchParams.get("equipment");
        if (eqId && (eqMatch || /\/temperature/i.test(u.pathname))) {
          navigate(`/temperature?equipment=${encodeURIComponent(eqId)}`);
          return;
        }
      } catch { /* fall through */ }
    }
    // 2. GS1 element-string QR (older labels or supplier deliveries) —
    //    parse the AIs and route by batch.
    if (looksLikeGs1(code)) {
      const gs1 = parseGs1(code);
      if (gs1?.batch) {
        navigate(`/labels/verify?batch=${encodeURIComponent(gs1.batch)}`);
        return;
      }
    }
    try {
      const { data } = await api.get("/equipment/by-barcode", {
        params: { code, site_id: activeSite.id },
      });
      if (!data) return;
      setEquipment(data);
    } catch {
      toast.error(`No equipment matches "${code}"`);
      setScannerOpen(true);
    }
  };

  if (!equipment) {
    // Camera overlay is the whole page in this mode — show a quick brand
    // intro behind it while the camera initialises so iOS doesn't flash
    // a white screen.
    return (
      <div className="fixed inset-0 z-40 bg-eucalyptus-900 text-white flex flex-col items-center justify-center text-center p-6">
        <ScanLine className="w-16 h-16 mb-4 animate-pulse" />
        <h1 className="font-display font-bold text-3xl mb-2">Scan an equipment sticker</h1>
        <p className="text-white/70 max-w-md">
          Aim your camera at the Code128 barcode on the fridge, freezer or probe.
          We&apos;ll pull up the right log automatically.
        </p>
        <BarcodeScanner
          open={scannerOpen}
          onClose={() => navigate("/")}
          onResult={handleScan}
          title="Quick scan"
        />
      </div>
    );
  }

  const actions = [
    {
      key: "temp",
      icon: Thermometer,
      label: "Log temperature",
      sub: "Open the form ready to type a reading",
      to: `/temperature?equipment=${equipment.id}`,
    },
    {
      key: "clean",
      icon: Sparkles,
      label: "Sign off cleaning",
      sub: "Filter the cleaning schedule to this area",
      to: `/cleaning?equipment=${equipment.id}`,
    },
    {
      key: "hist",
      icon: History,
      label: "View history",
      sub: "Last readings for this equipment",
      to: `/temperature?equipment=${equipment.id}&filter=eq`,
    },
  ];

  return (
    <div className="min-h-[80vh] flex flex-col items-center justify-center px-4 py-10">
      <div className="w-full max-w-md surface p-6 md:p-8" data-testid="quickscan-menu">
        <div className="label-eyebrow mb-2">Scanned equipment</div>
        <h1 className="font-display font-bold text-2xl md:text-3xl mb-1" data-testid="quickscan-eq-name">
          {equipment.name}
        </h1>
        <p className="text-sm text-charcoal-soft mb-1">
          {equipment.type.replace("_", " ")} · {equipment.location || "—"}
        </p>
        <p className="font-mono text-xs text-charcoal-soft mb-6">
          Target {equipment.target_min_c}° to {equipment.target_max_c}°C
        </p>

        <div className="space-y-3">
          {actions.map(({ key, icon: Icon, label, sub, to }) => (
            <Button
              key={key}
              data-testid={`quickscan-action-${key}`}
              onClick={() => navigate(to)}
              className="w-full h-auto py-4 px-5 bg-eucalyptus-900 hover:bg-eucalyptus-700 text-white justify-start text-left gap-4"
            >
              <Icon className="w-6 h-6 shrink-0" />
              <span className="flex flex-col items-start">
                <span className="font-bold text-base">{label}</span>
                <span className="text-xs text-white/70 font-normal">{sub}</span>
              </span>
            </Button>
          ))}
        </div>

        <div className="mt-6 flex gap-2">
          <Button
            data-testid="quickscan-rescan"
            variant="outline"
            onClick={() => {
              setEquipment(null);
              setScannerOpen(true);
            }}
            className="flex-1 h-12 border-eucalyptus-700 text-eucalyptus-900 gap-2"
          >
            <ScanLine className="w-4 h-4" /> Scan another
          </Button>
          <Button
            data-testid="quickscan-home"
            variant="outline"
            onClick={() => navigate("/")}
            className="flex-1 h-12 border-charcoal-soft/30 text-charcoal-soft gap-2"
          >
            <ArrowLeft className="w-4 h-4" /> Home
          </Button>
        </div>
      </div>
    </div>
  );
}
